/**
 * El SDK de JavaScript de Facebook, cargado una sola vez por página (D29).
 *
 * `FB.login` tiene que correr **sincrónicamente en el click**: si antes hay un `await`, el navegador ya
 * no lo considera un gesto del usuario y bloquea el popup. Por eso el SDK se precarga al montar la ficha
 * del canal, y el botón espera a que esté.
 */

export interface FacebookLoginResponse {
  status?: string;
  authResponse?: { code?: string | null } | null;
}

export interface FacebookSdk {
  init(opts: { appId: string; autoLogAppEvents?: boolean; xfbml?: boolean; version: string }): void;
  login(callback: (response: FacebookLoginResponse) => void, opts: Record<string, unknown>): void;
}

declare global {
  interface Window {
    FB?: FacebookSdk;
    fbAsyncInit?: () => void;
  }
}

const SDK_URL = "https://connect.facebook.net/en_US/sdk.js";
/** Un bloqueador de anuncios puede frenar el script sin disparar `onerror`: no se espera para siempre. */
const CARGA_MAX_MS = 20_000;

let cargando: Promise<FacebookSdk> | null = null;

/**
 * La promesa única del SDK, inicializado con la app de Meta. Si la carga falla, la próxima llamada lo
 * vuelve a intentar: un bloqueo momentáneo no deja el botón muerto hasta recargar.
 */
export function cargarSdkDeFacebook(appId: string, version: string): Promise<FacebookSdk> {
  if (cargando) return cargando;
  cargando = new Promise<FacebookSdk>((resolve, reject) => {
    let script: HTMLScriptElement | null = null;
    const fallar = (motivo: string) => {
      window.clearTimeout(limite);
      cargando = null;
      script?.remove();
      reject(new Error(motivo));
    };
    const listo = () => {
      window.clearTimeout(limite);
      const FB = window.FB;
      if (!FB) return fallar("El SDK de Facebook cargó sin definir FB");
      FB.init({ appId, autoLogAppEvents: true, xfbml: false, version });
      resolve(FB);
    };
    const limite = window.setTimeout(
      () => fallar("No se pudo cargar Facebook: puede estar bloqueado por una extensión del navegador"),
      CARGA_MAX_MS,
    );

    if (window.FB) return listo();
    window.fbAsyncInit = listo;
    script = document.createElement("script");
    script.src = SDK_URL;
    script.async = true;
    script.defer = true;
    script.crossOrigin = "anonymous";
    script.onerror = () =>
      fallar("No se pudo cargar Facebook: puede estar bloqueado por una extensión del navegador");
    document.body.appendChild(script);
  });
  return cargando;
}
